import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { AccountClientContactListSortCol, TAccountClientContactList, TAccountClientContactListResponse, TAccountClientContactListQuery, TAccountClientContactQueryPayload, TAccountClientContactListSortCol, adaptAccountClientContactFromListResponse, adaptAccountClientContactQueryToRequest } from '../../types';
import { BACKEND_ACCOUNT_CLIENT } from '../../backend-paths';


export const accountClientContactListGetApi = createAsyncThunk(
	`GET/${BACKEND_ACCOUNT_CLIENT}/:client_id/contacts`,
	(payload: TAccountClientContactQueryPayload, thunkApi) => {
		const { clientId, data } = payload;
		const path = `${BACKEND_ACCOUNT_CLIENT}/${clientId}/contacts`;

		const params = data ? adaptAccountClientContactQueryToRequest(data) : {};

		return HttpService.get<TAccountClientContactListResponse>(path, { params }).then(result => {
			return adaptAccountClientContactFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean,
	list: TAccountClientContactList,
	query: TAccountClientContactListQuery,
	error?: HttpError,
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: AccountClientContactListSortCol.CREATED_AT,
	},
	error: undefined,
};

export const accountClientContactListSlice = createSlice({
	name: 'accountClientContactListSlice',
	initialState,
	reducers: {
		setAccountClientContactListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setAccountClientContactListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setAccountClientContactListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setAccountClientContactListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setAccountClientContactListQuerySortCol(state, action: PayloadAction<TAccountClientContactListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearAccountClientContactList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[accountClientContactListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[accountClientContactListGetApi.fulfilled.type]: (state, action: PayloadAction<TAccountClientContactList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[accountClientContactListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useAccountClientContactListSelector = () => useAppSelector(state => state[accountClientContactListSlice.name]);

export const { clearAccountClientContactList, setAccountClientContactListQueryLimit, setAccountClientContactListQueryOffset, setAccountClientContactListQuerySort, setAccountClientContactListQuerySortCol, setAccountClientContactListQuerySearch } = accountClientContactListSlice.actions;
